import { useState, useEffect, useCallback, useRef } from 'react';
import { CasesApi } from '@/api';
import {
  DocumentWithStatus,
  ComprehensiveAnalysis,
  DocumentPaginationMeta,
} from '@/types/document';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';
// (Document type import removed if unused)

interface UseDocumentManagementOptions {
  caseId: string;
  enablePolling?: boolean;
  pollingInterval?: number;
  maxPollingDuration?: number;
  /** Called when a batch of uploaded documents finishes processing */
  onBatchComplete?: () => void;
}

export const useDocumentManagement = ({
  caseId,
  enablePolling = true,
  pollingInterval = 10000,
  maxPollingDuration = 120000, // 2 minutes
  onBatchComplete,
}: UseDocumentManagementOptions) => {
  const [documents, setDocuments] = useState<DocumentWithStatus[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState<
    DocumentWithStatus[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [criteriaAnalysis, setCriteriaAnalysis] =
    useState<ComprehensiveAnalysis | null>(null);
  // Initialize pagination from localStorage (per-case) synchronously
  const initialDocPageSize = (() => {
    if (typeof window === 'undefined') return 20;
    const raw = localStorage.getItem(`case-${caseId}-docs-page-size`);
    const n = raw ? parseInt(raw, 10) : 20;
    return !isNaN(n) && n > 0 ? n : 20;
  })();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialDocPageSize);
  const [meta, setMeta] = useState<DocumentPaginationMeta | null>(null);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const idlePollsRef = useRef(0); // number of consecutive polls with no active processing docs
  const pollsSinceLastCriteriaRefreshRef = useRef(0); // Fallback counter for criteria refresh
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);

  // Track batch uploads to avoid excessive criteria recalculations
  const pendingUploadBatchRef = useRef<Set<string>>(new Set());
  // Classification of processing statuses (uppercase for normalization)
  const ACTIVE_STATUSES = useRef(
    new Set([
      'PENDING',
      'SCANNING',
      'PENDING_OCR',
      'OCR_PROCESSING',
      'OCR_COMPLETED', // After OCR, still needs indexing, summarization, criteria matching
      'AI_CATEGORIZING',
      'INDEXING',
      'RETRY_PENDING',
    ])
  );
  const SUCCESS_STATUSES = useRef(new Set(['COMPLETED']));
  const FAILURE_STATUSES = useRef(
    new Set([
      'FAILED',
      'SCAN_FAILED',
      'OCR_FAILED',
      'AI_FAILED',
      'INDEX_FAILED',
    ])
  );
  const TERMINAL_STATUSES = useRef(
    new Set([
      'COMPLETED',
      'FAILED',
      'SCAN_FAILED',
      'OCR_FAILED',
      'AI_FAILED',
      'INDEX_FAILED',
      'ARCHIVED',
    ])
  );

  // Unified transformation for document objects
  const transformDocument = useCallback((doc: any): DocumentWithStatus => {
    const status = String(doc.processingStatus || '').toUpperCase();
    // Human readable labels (non-technical, no fake %)
    const labelMap: Record<string, string> = {
      PENDING: 'Queued',
      SCANNING: 'Scanning',
      PENDING_OCR: 'Waiting for OCR',
      OCR_PROCESSING: 'Reading file',
      OCR_COMPLETED: 'Analyzing content',
      AI_CATEGORIZING: 'AI categorizing',
      INDEXING: 'Indexing',
      RETRY_PENDING: 'Retry queued',
      COMPLETED: 'Ready',
      FAILED: 'Failed',
      SCAN_FAILED: 'Scan failed',
      OCR_FAILED: 'OCR failed',
      AI_FAILED: 'AI failed',
      INDEX_FAILED: 'Index failed',
      ARCHIVED: 'Archived',
    };

    const friendly = labelMap[status] || 'Unknown';
    const isActive = ACTIVE_STATUSES.current.has(status);
    const isDone = SUCCESS_STATUSES.current.has(status);
    const isFailed = FAILURE_STATUSES.current.has(status);

    return {
      ...doc,
      uploadStatus: isDone
        ? 'completed'
        : isFailed
          ? 'failed'
          : isActive
            ? 'processing'
            : undefined,
      isUploading: isActive && !isDone && !isFailed,
      uploadProgress: isDone ? 100 : undefined,
      // Expose a user-friendly label separate from internal uploadStatus
      displayStatusLabel: friendly,
    } as DocumentWithStatus & { displayStatusLabel?: string };
  }, []);

  const loadDocuments = useCallback(
    async (
      overridePage?: number,
      overrideSize?: number,
      { preserveScroll = false } = {}
    ) => {
      if (!caseId) return;
      const p = overridePage || page;
      const ps = overrideSize || pageSize;
      try {
        setIsLoading(true);
        setError(null);
        const previousScroll = preserveScroll ? window.scrollY : null;
        const response = await CasesApi.getCaseDocuments(caseId, p, ps);
        const documentsData =
          (response as any).items ||
          (response as any).documents ||
          (response as any).data ||
          [];
        const metaData = (response as any).meta || null;
        if (metaData) setMeta(metaData);
        const convertedDocs: DocumentWithStatus[] =
          documentsData.map(transformDocument);
        setDocuments(convertedDocs);
        // If caller explicitly overrode page/pageSize, persist them in state & refs
        if (overridePage && overridePage !== page) setPage(overridePage);
        if (overrideSize && overrideSize !== pageSize)
          setPageSize(overrideSize);
        if (preserveScroll && previousScroll !== null) {
          requestAnimationFrame(() => {
            window.scrollTo({ top: previousScroll });
          });
        }
      } catch (err) {
        console.error('Failed to load documents:', err);
        setError('Failed to load documents');
      } finally {
        setIsLoading(false);
      }
    },
    [caseId, page, pageSize, transformDocument]
  );

  // Load criteria analysis for case
  const loadCriteriaAnalysis = useCallback(async () => {
    if (!caseId) return;

    try {
      const response = await CasesApi.getCaseAnalysis(caseId);
      setCriteriaAnalysis(response.data);
    } catch (err) {
      console.error('Failed to load criteria analysis:', err);
    }
  }, [caseId]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    setIsPolling(false);
  }, []);

  // Start polling for document status updates
  const startPolling = useCallback(() => {
    if (!enablePolling || isPolling) return;

    setIsPolling(true);
    idlePollsRef.current = 0;

    const pollForUpdates = async () => {
      try {
        // Use refs to avoid stale closure over page/pageSize
        const response = await CasesApi.getCaseDocuments(
          caseId,
          pageRef.current,
          pageSizeRef.current
        );
        const documentsData =
          (response as any).items ||
          (response as any).documents ||
          (response as any).data ||
          [];
        const metaData = (response as any).meta || null;
        if (metaData) setMeta(metaData);

        // Convert API documents to DocumentWithStatus
        const updatedDocs: DocumentWithStatus[] =
          documentsData.map(transformDocument);
        const previousScroll = window.scrollY;

        // Check if any documents from the pending batch have completed
        const completedBatchDocs = updatedDocs.filter(
          doc =>
            pendingUploadBatchRef.current.has(doc.id) &&
            (SUCCESS_STATUSES.current.has(
              String(doc.processingStatus || '').toUpperCase()
            ) ||
              FAILURE_STATUSES.current.has(
                String(doc.processingStatus || '').toUpperCase()
              ))
        );

        // Remove completed documents from pending batch
        completedBatchDocs.forEach(doc => {
          pendingUploadBatchRef.current.delete(doc.id);
        });

        // Check if entire batch is done AND we just completed it this poll
        const batchJustCompleted =
          completedBatchDocs.length > 0 &&
          pendingUploadBatchRef.current.size === 0;

        setDocuments(updatedDocs);
        requestAnimationFrame(() => {
          window.scrollTo({ top: previousScroll });
        });

        const hasProcessingDocuments = updatedDocs.some(doc =>
          ACTIVE_STATUSES.current.has(
            String(doc.processingStatus || '').toUpperCase()
          )
        );

        if (hasProcessingDocuments) {
          idlePollsRef.current = 0;
        } else {
          idlePollsRef.current += 1;
        }

        // Increment fallback counter
        pollsSinceLastCriteriaRefreshRef.current += 1;

        // Flag to track if we refreshed criteria this poll
        let didRefreshCriteria = false;

        // Only refresh criteria when ENTIRE batch completes (not per-document)
        if (batchJustCompleted) {
          console.log(
            '🎯 Batch upload complete - triggering criteria recalculation'
          );
          try {
            await loadCriteriaAnalysis();
            pollsSinceLastCriteriaRefreshRef.current = 0;
            didRefreshCriteria = true;
          } catch {}

          // Trigger onBatchComplete callback after a delay to allow backend
          // folder reorganization to complete (auto-triggered after document processing)
          if (onBatchComplete) {
            console.log('📁 Scheduling folder refresh after batch complete...');
            setTimeout(() => {
              console.log('📁 Triggering folder refresh after backend reorganization');
              onBatchComplete();
            }, 3000); // 3 second delay to allow backend reorganization
          }
        }

        // Fallback: Refresh criteria after 4 polls (~40s) even if batch hasn't completed
        // This prevents criteria from never showing if documents fail
        if (!didRefreshCriteria && pollsSinceLastCriteriaRefreshRef.current >= 4) {
          console.log('⏰ Fallback criteria refresh triggered (4 polls elapsed)');
          try {
            await loadCriteriaAnalysis();
            pollsSinceLastCriteriaRefreshRef.current = 0;
            didRefreshCriteria = true;
          } catch {}
        }

        if (!hasProcessingDocuments && idlePollsRef.current >= 3) {
          stopPolling();
          // Refresh criteria one final time after all processing completes
          if (pendingUploadBatchRef.current.size === 0 && !didRefreshCriteria) {
            try {
              await loadCriteriaAnalysis();
              pollsSinceLastCriteriaRefreshRef.current = 0;
            } catch {}
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    // Start polling immediately
    pollForUpdates();

    // Set up interval polling
    pollingIntervalRef.current = setInterval(pollForUpdates, pollingInterval);

    // Set timeout to stop polling after max duration
    pollingTimeoutRef.current = setTimeout(() => {
      stopPolling();
    }, maxPollingDuration);
  }, [
    caseId,
    enablePolling,
    isPolling,
    pollingInterval,
    maxPollingDuration,
    stopPolling,
    loadCriteriaAnalysis,
    onBatchComplete,
  ]);

  // Upload documents in parallel with concurrency limit
  const uploadDocuments = useCallback(
    async (files: File[]) => {
      const uploadStartTime = Date.now();

      // Track upload start
      analytics.track(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_STARTED, {
        case_id: caseId,
        file_count: files.length,
        total_size_kb: Math.round(
          files.reduce((sum, f) => sum + f.size, 0) / 1024
        ),
      });

      try {
        setError(null);

        // Create placeholder documents for UI with uploading status
        const placeholders: DocumentWithStatus[] = files.map((file, idx) => ({
          id: `uploading-${Date.now()}-${idx}`,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          processingStatus: 'UPLOADING' as any,
          isProcessed: false,
          uploadedAt: new Date().toISOString(),
          addedAt: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          filePath: '',
          documentType: '',
          tags: [],
          categoryConfidence: 0,
          ocrMethod: '',
          role: '',
        }));

        // Add placeholders to uploading documents
        setUploadingDocuments(placeholders);

        const allNewDocs: DocumentWithStatus[] = [];
        const uploadedFileIds = new Set<string>();
        const failedFileIds = new Set<string>();

        // Parallel upload with concurrency limit (10 files at a time for faster uploads)
        const CONCURRENT_UPLOADS = 10;
        const uploadPromises: Promise<void>[] = [];

        for (let i = 0; i < files.length; i += CONCURRENT_UPLOADS) {
          const batch = files.slice(i, i + CONCURRENT_UPLOADS);
          const batchPromises = batch.map(async (file, batchIdx) => {
            const fileIdx = i + batchIdx;
            const placeholderId = `uploading-${Date.now()}-${fileIdx}`;
            const fileUploadStart = Date.now();

            try {
              const response = await CasesApi.uploadDocuments(caseId, [file]);

              if ((response as any).success && (response as any).documents) {
                const newDocs: DocumentWithStatus[] = (
                  response as any
                ).documents.map(transformDocument);
                allNewDocs.push(...newDocs);
                uploadedFileIds.add(placeholderId);

                // Track successful upload for each file
                analytics.track(ANALYTICS_EVENTS.DOCUMENT_UPLOADED, {
                  case_id: caseId,
                  file_type: file.name.split('.').pop() || 'unknown',
                  file_size_kb: Math.round(file.size / 1024),
                  upload_duration_ms: Date.now() - fileUploadStart,
                });

                // Remove this file from uploading list
                setUploadingDocuments(prev =>
                  prev.filter(doc => doc.id !== placeholderId)
                );
              }
            } catch (fileErr) {
              console.error(`Failed to upload ${file.name}:`, fileErr);
              failedFileIds.add(placeholderId);

              // Track upload failure
              analytics.track(ANALYTICS_EVENTS.DOCUMENT_UPLOAD_FAILED, {
                case_id: caseId,
                file_type: file.name.split('.').pop() || 'unknown',
                file_size_kb: Math.round(file.size / 1024),
                error_message:
                  fileErr instanceof Error ? fileErr.message : 'Unknown error',
              });

              // Mark as failed in uploading list
              setUploadingDocuments(prev =>
                prev.map(doc =>
                  doc.id === placeholderId
                    ? { ...doc, processingStatus: 'FAILED' as any }
                    : doc
                )
              );
            }
          });

          // Wait for current batch to complete before starting next batch
          await Promise.all(batchPromises);
        }

        // Clear uploading documents
        setUploadingDocuments([]);

        const successCount = uploadedFileIds.size;
        const failCount = failedFileIds.size;

        // Reload documents after all uploads complete
        if (successCount > 0) {
          await loadDocuments(1, pageSizeRef.current, { preserveScroll: true });
          setPage(1);

          // Track uploaded document IDs for batch completion detection
          pendingUploadBatchRef.current.clear();
          allNewDocs.forEach(doc => {
            pendingUploadBatchRef.current.add(doc.id);
          });

          console.log(
            `📦 Starting batch tracking for ${allNewDocs.length} uploaded documents`
          );

          const hasProcessingDocs = allNewDocs.some(doc =>
            ACTIVE_STATUSES.current.has(
              String(doc.processingStatus || '').toUpperCase()
            )
          );
          if (hasProcessingDocs) {
            stopPolling();
            startPolling();
          }
        }

        // Show summary message
        if (failCount > 0) {
          setError(
            `Uploaded ${successCount} files successfully, ${failCount} failed`
          );
        }
      } catch (err) {
        console.error('Upload failed:', err);
        setError('Failed to upload documents');
        setUploadingDocuments([]);

        // Track general upload failure
        analytics.track(ANALYTICS_EVENTS.ERROR_OCCURRED, {
          error_type: 'upload_failed',
          error_message: err instanceof Error ? err.message : 'Un known error',
          case_id: caseId,
        });

        throw err;
      }
    },
    [
      caseId,
      startPolling,
      stopPolling,
      loadDocuments,
      pageSize,
      transformDocument,
    ]
  );

  // Retry document processing
  const retryDocument = useCallback(
    async (documentId: string) => {
      try {
        await CasesApi.retryDocument(documentId);

        // Update document status to processing
        setDocuments(prev =>
          prev.map(doc =>
            doc.id === documentId
              ? { ...doc, processingStatus: 'PROCESSING' }
              : doc
          )
        );

        // Start polling
        startPolling();
      } catch (err) {
        console.error('Retry failed:', err);
        throw err;
      }
    },
    [startPolling]
  );

  // Delete document
  const deleteDocument = useCallback(
    async (documentId: string) => {
      try {
        await CasesApi.deleteDocument(caseId, documentId);
        setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      } catch (err) {
        console.error('Delete failed:', err);
        throw err;
      }
    },
    [caseId]
  );

  // Rename document
  const renameDocument = useCallback(
    async (documentId: string, customFileName: string) => {
      try {
        const response = await CasesApi.renameDocument(
          caseId,
          documentId,
          customFileName
        );

        // Immediately update the local state with the new customFileName
        // Backend returns { success, document } not { success, data }
        if (response.success && (response as any).document) {
          const updatedDoc = (response as any).document;
          setDocuments(prev =>
            prev.map(doc =>
              doc.id === documentId
                ? { ...doc, customFileName: updatedDoc.customFileName }
                : doc
            )
          );
        }

        return response;
      } catch (err) {
        console.error('Rename failed:', err);
        throw err;
      }
    },
    [caseId]
  );

  // Get criteria for document
  const getDocumentCriteria = useCallback(
    (documentId: string) => {
      if (!criteriaAnalysis) return [];

      return criteriaAnalysis.strengthAnalysis.criteriaAnalysis.filter(
        criteria => {
          // supportingDocuments can be array of strings or array of objects with documentId
          return criteria.supportingDocuments.some(doc =>
            typeof doc === 'string'
              ? doc === documentId
              : doc.documentId === documentId
          );
        }
      );
    },
    [criteriaAnalysis]
  );

  // Initial load
  useEffect(() => {
    if (caseId) {
      loadDocuments(undefined, undefined, { preserveScroll: true });
      loadCriteriaAnalysis();
    }
  }, [caseId, loadDocuments, loadCriteriaAnalysis]);

  // Persist page size per case
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(`case-${caseId}-docs-page-size`, String(pageSize));
    }
  }, [caseId, pageSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Auto-start polling after page refresh if there are still active processing documents
  useEffect(() => {
    if (!enablePolling) return;
    if (isPolling) return;
    if (documents.length === 0) return;
    const hasActive = documents.some(doc =>
      ACTIVE_STATUSES.current.has(
        String(doc.processingStatus || '').toUpperCase()
      )
    );
    if (hasActive) {
      startPolling();
    }
  }, [documents, enablePolling, isPolling, startPolling]);

  // Keep refs in sync so polling uses latest pagination values
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  return {
    documents,
    uploadingDocuments,
    isLoading,
    error,
    isPolling,
    criteriaAnalysis,

    // Actions
    loadDocuments,
    uploadDocuments,
    retryDocument,
    deleteDocument,
    renameDocument,
    getDocumentCriteria,
    startPolling,
    stopPolling,
    loadCriteriaAnalysis,
    // Pagination
    page,
    pageSize,
    setPage,
    setPageSize,
    meta,
  };
};
